import { useAuthenticator } from "@aws-amplify/ui-react";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { SpinnerOverlay } from "components/SpinnerOverlay";
import { usePageTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearAuthFlowCookie, setAuthFlowCookie } from "utils/authFlowCookie";

export function IDPAuthCallback() {
  usePageTitle("Signing you in");
  const { authStatus } = useAuthenticator();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const hasProcessedCode = useRef(false);

  // Manual token exchange function for IDP-initiated flows
  const exchangeCodeForTokens = useCallback(async (code: string) => {
    try {
      console.log("Exchanging OAuth code for tokens via backend...");

      const redirectUri = `${window.location.origin}/auth/callback`;

      // Determine the API endpoint based on environment
      const apiBaseUrl =
        import.meta.env.VITE_API_URL ||
        (window.location.hostname === "localhost" ? "http://localhost:4000" : "");

      // Call our backend token exchange endpoint
      const tokenResponse = await fetch(`${apiBaseUrl}/api/auth/token-exchange`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: code,
          redirectUri: redirectUri,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error || "Token exchange failed");
      }

      const response = await tokenResponse.json();

      if (!response.success || !response.tokens) {
        throw new Error(response.error || "Token exchange failed");
      }

      const { tokens } = response;
      console.log("Successfully received tokens from backend");

      // Parse the ID token to get user information
      const idTokenPayload = JSON.parse(atob(tokens.idToken.split(".")[1]));
      const username = idTokenPayload["cognito:username"] || idTokenPayload.sub;

      // Store tokens using Amplify's token provider
      // The tokens need to be stored with specific keys that Amplify expects
      const tokenData = {
        accessToken: {
          payload: JSON.parse(atob(tokens.accessToken.split(".")[1])),
          toString: () => tokens.accessToken,
        },
        idToken: {
          payload: idTokenPayload,
          toString: () => tokens.idToken,
        },
        refreshToken: tokens.refreshToken,
        clockDrift: 0,
        username: username,
        signInDetails: {
          loginId: username,
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      };
      // @ts-expect-error
      await cognitoUserPoolsTokenProvider.authTokenStore.storeTokens(tokenData);
      console.log("Tokens stored successfully, reloading to complete authentication");

      // Reload the page to let Amplify pick up the stored tokens
      window.location.href = "/";
    } catch (err) {
      console.error("Error during manual token exchange:", err);
      setError("Failed to complete authentication. Please try again.");
      clearAuthFlowCookie();
    }
  }, []);

  useEffect(() => {
    console.log("IDP auth callback - authStatus:", authStatus);

    // Set the auth flow cookie for IDP
    setAuthFlowCookie("idp");

    // Check for errors in URL first
    const urlParams = new URLSearchParams(location.search);
    const errorParam = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");

    if (errorParam) {
      console.error("OAuth error from Cognito:", errorParam, errorDescription);

      // Handle specific SAML replay error
      if (
        errorDescription?.includes("Assertion ID in the SAML assertion has already been presented")
      ) {
        setError(
          "This login link has already been used. Please initiate a new login from your identity provider."
        );
      } else {
        setError(errorDescription || "Authentication failed. Please try again.");
      }
      clearAuthFlowCookie();
      return;
    }

    // Check if we have a code parameter
    const code = urlParams.get("code");

    if (!code && authStatus === "unauthenticated") {
      // Only show error if there's no code AND we're unauthenticated
      // (authenticated users might navigate here directly)
      setError(
        "Invalid authentication request. Please initiate login from your identity provider."
      );
      clearAuthFlowCookie();
      return;
    }

    // If we have a code and still unauthenticated we need to manually exchange the code for tokens
    if (code && authStatus === "unauthenticated" && !hasProcessedCode.current) {
      console.log(
        "IDP-initiated flow detected (code without state). Manually exchanging tokens..."
      );
      hasProcessedCode.current = true;
      exchangeCodeForTokens(code);
      return;
    }

    if (authStatus === "authenticated") {
      console.log("IDP authentication successful, redirecting to home");
      navigate("/", { replace: true });
    }
  }, [authStatus, navigate, location, exchangeCodeForTokens]);

  if (error) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          maxWidth: 500,
          margin: "100px auto",
          border: "1px solid #ddd",
          borderRadius: 8,
          backgroundColor: "#f9f9f9",
        }}
      >
        <h2 style={{ color: "#d32f2f" }}>Authentication Error</h2>
        <p style={{ marginBottom: 20 }}>{error}</p>
        <div style={{ marginTop: 30 }}>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 15 }}>To resolve this issue:</p>
          <ol style={{ textAlign: "left", display: "inline-block", fontSize: 14 }}>
            <li>Return to your identity provider's portal</li>
            <li>Initiate a new login to Encamp</li>
            <li>Or contact your administrator for assistance</li>
          </ol>
        </div>
        <a
          href="/"
          style={{
            marginTop: 20,
            display: "inline-block",
            color: "#1976d2",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Return to Home
        </a>
      </div>
    );
  }

  // Show spinner while authenticating
  if (authStatus === "configuring") {
    return <SpinnerOverlay loading />;
  }

  // If unauthenticated and no error set yet, OAuth might still be processing
  if (authStatus === "unauthenticated") {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");
    if (code) {
      // We have a code but still unauthenticated, OAuth is likely processing
      console.log("OAuth code present, waiting for authentication to complete...");
      return <SpinnerOverlay loading />;
    }
  }

  // Default loading state
  return <SpinnerOverlay loading />;
}
